import React from "react";
import { Controller } from "react-hook-form";
import { TextInput } from "djinn-components";
import Form from "../../Form";
import SiteImageHolder from "./SiteImageHolder";
import FormFieldErrors from "../../Form/FormFieldErrors";
import { toast } from "react-toastify";
import { useSiteState } from "../../../utils/useSiteState";

const ProjectDetailsForm: React.FC = () => {
  const { site } = useSiteState();
  const getDefaults = () => {
    const { headline, subtext, logo, featured_image, is_coming_soon } = site;

    return {
      headline,
      subtext,
      logo,
      featured_image,
      is_coming_soon,
    };
  };

  const onSubmit = async (responseData: any) => {
    toast("Site content saved", { type: "success" });
  };

  return (
    <Form
      getDefaults={getDefaults}
      onCompleted={onSubmit}
      buttonTitle="Update Content"
      endpoint={`sites/${site.id}`}
      method="put"
      className="min-h-[600px]"
      render={({ errors, control }) => (
        <>
          <div className="mb-4 mt-10">
            <label className="mb-2 block ml-4 text-sm font-medium">
              Headline
            </label>
            <Controller
              name="headline"
              control={control}
              defaultValue=""
              rules={{
                required: true,
              }}
              render={({ field: { onChange, value } }: any): JSX.Element => (
                <TextInput
                  value={value}
                  onChange={onChange}
                  invalid={!!errors.headline}
                  placeholder="Site headline"
                />
              )}
            />

            <FormFieldErrors
              errors={errors}
              field="headline"
              requiredMessage="Site headline is required"
            />
          </div>

          <div className="mb-4 mt-6">
            <label className="mb-2 block ml-4 text-sm font-medium">
              Sub Text
            </label>

            <Controller
              name="subtext"
              control={control}
              defaultValue=""
              rules={{
                required: false,
              }}
              render={({ field: { onChange, value } }: any): JSX.Element => (
                <TextInput
                  value={value}
                  type="textarea"
                  onChange={onChange}
                  invalid={!!errors.subtext}
                  placeholder="Site subtext"
                />
              )}
            />

            <FormFieldErrors errors={errors} field="subtext" />
          </div>

          <div className="mb-4 mt-6">
            <Controller
              name="logo"
              control={control}
              render={({ field: { onChange, value } }: any): JSX.Element => (
                <SiteImageHolder
                  setImage={onChange}
                  id="logo"
                  type="app_logo"
                  image={value}
                  sClass="w-3/4 h-24"
                  imageBg="bg-gray-200"
                  label="Site logo"
                  description="This logo will be displayed in the site top bar."
                  imagePlaceholder="/logo-placeholder.png"
                >
                  <ul>
                    <li>Logo must be visible on a light background</li>
                    <li>Recommended image 400px x 160px</li>
                    <li>Maximum file size of 1mb.</li>
                  </ul>
                </SiteImageHolder>
              )}
            />
            <FormFieldErrors
              errors={errors}
              field="logo"
              requiredMessage="A logo is required"
            />
          </div>

          <div className="mb-4">
            <Controller
              name="featured_image"
              control={control}
              render={({ field: { onChange, value } }: any): JSX.Element => (
                <SiteImageHolder
                  setImage={onChange}
                  id="featured_image"
                  type="featured_image"
                  image={value}
                  sClass="w-3/4 h-24"
                  imageBg="bg-gray-200"
                  label="Site feature image"
                  description="This feature image will be displayed the main content area of the site."
                  imagePlaceholder="/image-placeholder.png"
                >
                  <ul>
                    <li>Feature must be visible on a light background</li>
                    <li>Recommended image 800px x 800px</li>
                    <li>Maximum file size of 1mb.</li>
                  </ul>
                </SiteImageHolder>
              )}
            />
            <FormFieldErrors errors={errors} field="featured_image" />
          </div>
        </>
      )}
    />
  );
};

export default ProjectDetailsForm;
